<div class="dashboard">

  <!-- KPIs Section -->
  <h3 class="title mb-3">Key Performance Indicators</h3>

  <div class="kpi-grid">
    @if (isLoading()) {
    @for (i of [1,2,3]; track i) {
    <div class="kpi-card shadow" style="padding: 24px;">
      <div class="d-flex align-items-center gap-3">
        <p-skeleton shape="circle" size="3.5rem"></p-skeleton>
        <div class="flex-grow-1">
          <p-skeleton width="50%" height="2rem" styleClass="mb-2"></p-skeleton>
          <p-skeleton width="30%" height="1rem"></p-skeleton>
        </div>
      </div>
    </div>
    }
    } @else {
    @for (kpi of kpis; track kpi.label) {
    <div class="kpi-card shadow">
      <div class="kpi">
        <div class="kpi-icon-img-wrapper" [ngStyle]="{'background-color': kpi.color}">
          <img [src]="kpi.image" [alt]="kpi.label" class="kpi-icon-img" />
        </div>
        <div class="kpi-info">
          <div class="stat-count" [countUp]="kpi.value"
            [prefix]="kpi.prefix || (kpi.label === 'Total Revenue' ? '$' : '')" [duration]="2000"></div>
          <p class="mb-0 text-muted small">{{ kpi.label }}</p>
        </div>
      </div>
    </div>
    }
    }
  </div>

  <!-- Revenue Chart -->
  <div class="chart-card shadow">
    <div class="chart-header">
      <h2>{{ chartTitle }}</h2>
      <div>
        <p-button icon="pi pi-ellipsis-v" styleClass="p-button-rounded p-button-text p-button-plain"
          (click)="chartMenu.toggle($event)"></p-button>
        <p-menu #chartMenu [popup]="true" [model]="chartMenuItems" appendTo="body"></p-menu>
      </div>
    </div>
    <div class="chart-wrapper">
      @if (isLoading()) {
      <div class="p-3"><p-skeleton width="100%" height="300px"></p-skeleton></div>
      } @else {
      <p-chart type="line" [data]="chartData" [options]="chartOptions" [plugins]="chartPlugins"
        [style]="{ height: '300px' }">
      </p-chart>
      }
    </div>
  </div>

  <!-- Tables Row -->
  <div class="tables-row">
    <!-- Recent Lobbies -->
    <div class="table-card shadow">
      <div class="table-header">
        <h2>Recent Lobbies</h2>
      </div>
      <div class="table-responsive">
        @if (isLoading()) {
        <table class="dashboard-table">
          <thead>
            <tr>
              <th>Host</th>
              <th class="text-center">Interest</th>
              <th class="text-center">Participants</th>
              <th class="text-center">Revenue</th>
            </tr>
          </thead>
          <tbody>
            @for (i of [1,2,3,4,5]; track i) {
            <tr>
              <td>
                <div class="d-flex align-items-center gap-2">
                  <p-skeleton shape="circle" size="32px"></p-skeleton>
                  <p-skeleton width="120px" height="1.2rem"></p-skeleton>
                </div>
              </td>
              <td class="text-center"><div class="d-flex justify-content-center"><p-skeleton width="30px" height="1.2rem"></p-skeleton></div></td>
              <td class="text-center"><div class="d-flex justify-content-center"><p-skeleton width="40px" height="1.2rem"></p-skeleton></div></td>
              <td class="text-center"><div class="d-flex justify-content-center"><p-skeleton width="50px" height="1.2rem"></p-skeleton></div></td>
            </tr>
            }
          </tbody>
        </table>
        } @else {
        <table class="dashboard-table">
          <thead>
            <tr>
              <th>Host</th>
              <th class="text-center">Interest</th>
              <th class="text-center">Participants</th>
              <th class="text-center">Revenue</th>
            </tr>
          </thead>
          <tbody>
            @for (lobby of recentLobbies; track lobby._id || lobby.id || $index) {
            <tr class="clickable-row" (click)="navigateToLobby(lobby._id || lobby.id)">
              <td>
                <div class="d-flex align-items-center gap-2">
                  <div class="avatar-small"
                    [class.avatar-img-wrap]="lobby.host?.ProfilePhotos && lobby.host.ProfilePhotos.length > 0">
                    @if (lobby.host?.ProfilePhotos && lobby.host.ProfilePhotos.length > 0) {
                    <img [src]="lobby.host.ProfilePhotos[0].signedUrl || lobby.host.ProfilePhotos[0].url" alt="Profile"
                      class="avatar-photo" />
                    } @else {
                    <span>
                      {{ getLobbyHostInitials(lobby) }}
                    </span>
                    }
                  </div>
                  <div class="d-flex align-items-center">
                    <span class="fw-semibold me-2 user-name-clip" [pTooltip]="getLobbyHostName(lobby)" tooltipPosition="top">{{ getLobbyHostName(lobby) | titlecase }}</span>
                    <span class="status-dot-indicator" [ngClass]="'dot-' + (lobby.status || 'pending').toLowerCase()"
                      [title]="lobby.status"></span>
                  </div>
                </div>
              </td>
              <td class="text-center">
                <span>{{ lobby.interestsCount ? (lobby.currentInterestIdx ?? 0) + 1 : 0 }}/{{ lobby.interestsCount ?? lobby.interestsList?.length ?? 0 }}</span>
              </td>
              <td class="text-center">{{ lobby.currentParticipants ?? lobby.participants ?? 0 }}/{{ lobby.maxParticipants ?? lobby.participantsCount ?? lobby.capacity ?? 0 }}</td>
              <td class="fw-semibold text-success text-center">${{ lobby.revenueGenerated ?? lobby.revenue ?? 0 }}</td>
            </tr>
            }
          </tbody>
        </table>
        }
      </div>
    </div>

    <!-- Recent Added Users -->
    <div class="table-card shadow">
      <div class="table-header">
        <h2>Recent Added Users</h2>
      </div>
      <div class="table-responsive">
        @if (isLoading()) {
        <table class="dashboard-table">
          <thead>
            <tr>
              <th>User</th>
              <th>Email</th>
              <th>Country</th>
              <th>Joined At</th>
            </tr>
          </thead>
          <tbody>
            @for (i of [1,2,3,4,5]; track i) {
            <tr>
              <td>
                <div class="d-flex align-items-center gap-2">
                  <p-skeleton shape="circle" size="32px"></p-skeleton>
                  <p-skeleton width="100px" height="1.2rem"></p-skeleton>
                </div>
              </td>
              <td><p-skeleton width="140px" height="1.2rem"></p-skeleton></td>
              <td><p-skeleton width="60px" height="1.2rem"></p-skeleton></td>
              <td><p-skeleton width="80px" height="1.2rem"></p-skeleton></td>
            </tr>
            }
          </tbody>
        </table>
        } @else {
        <table class="dashboard-table">
          <thead>
            <tr>
              <th>User</th>
              <th>Email</th>
              <th>Country</th>
              <th>Joined At</th>
            </tr>
          </thead>
          <tbody>
            @for (user of recentUsers; track user._id || user.id || $index) {
            <tr class="clickable-row" (click)="navigateToUser(user.email || user._id || user.id)">
              <td>
                <div class="d-flex align-items-center gap-2">
                  <div class="avatar-small"
                    [class.avatar-img-wrap]="user.ProfilePhotos && user.ProfilePhotos.length > 0">
                    @if (user.ProfilePhotos && user.ProfilePhotos.length > 0) {
                    <img [src]="user.ProfilePhotos[0].signedUrl || user.ProfilePhotos[0].url" alt="Profile"
                      class="avatar-photo" />
                    } @else {
                    <span>
                      {{ getUserInitials(user) }}
                    </span>
                    }
                  </div>
                  <div class="fw-semibold user-name-clip" [pTooltip]="getUserName(user)" tooltipPosition="top">{{ getUserName(user) | titlecase }}</div>
                </div>
              </td>

              <td>
                <div class="email-clip" [pTooltip]="user.email || 'N/A'" tooltipPosition="top">
                  {{ user.email || 'N/A' }}
                </div>
              </td>
              <td>{{ user.Country || user.location?.country || 'N/A' }}</td>
              <td class="text-muted">{{ user.createdAt | date: 'dd-MM-yyyy' }}</td>
            </tr>
            }
          </tbody>
        </table>
        }
      </div>
    </div>
  </div>
</div>